/**
 * Converts an RGeo point string to lat, long
 * @param {string} rgeo - point in RGeo String format
 * @returns {[number, number]} [latitude, longitude]
 */
export function rgeoToLatLng(rgeo) {
  let [, lngStr, latStr] = rgeo.match(/POINT\s*\(\s*(\S+)\s+(\S+)\s*\)/);
  return [latStr, lngStr].map(parseFloat);
}

/**
 * Converts lat, long into GeoJSON point
 * @param {[number, number]} latLng [latitude, longitude]
 */
export function latLngToGeoJSON([lat, long]) {
  return { type: "Point", coordinates: [long, lat] }
}

/**
 * Converts lat, long into RGeo point string
 * @param {[number, number]} param0 - [latitude, longitude]
 * @returns {string} point in RGeo String format
 */
export function latLngToRgeo([lat, lng]) {
  return `POINT(${lng} ${lat})`;
}

/**
 * Converts lat, long array into RGeo polygon string
 * @param {[number, number][][]} latLngs - [[[latitude, longitude], ...], ...]
 * @returns {string} polygon in RGeo String format
 */
export function latLngsToRgeo(latLngs) {
  let coordinates = latLngs.map(points => {
    let closedLoop = [...points, points[0]];
    return closedLoop.map(([lat, lng]) => `${lng} ${lat}`).join(', ');
  });
  return `POLYGON(${coordinates.map(x => `(${x})`).join(',')})`;
}

/**
 * Converts lat, long array into GeoJSON polygon
 * @param {[number, number][][]} latLngs - [[[latitude, longitude], ...], ...]
 * @returns {object} polygon in GeoJSON format
 */
export function latLngsToGeoJSON(latLngs) {
  return { type: "Polygon", coordinates: latLngs.map(points => points.map(([lat, lng]) => [lng, lat])) };
}

/**
 * Converts an RGeo polygon string to GeoJSON
 * @param {string} rgeo - polygon in RGeo String format
 * @returns {object} polygon in GeoJSON format
 */
export function rgeoPolygonToGeoJSON(rgeo) {
  let coordinates = [];
  for (let [,part] of rgeo.matchAll(/\(([^()]+)\)/g)) {
    let points = part.trim().split(',');
    points.pop();
    coordinates.push(points.map(s => s.trim().split(' ').map(parseFloat)));
  }

  return { type: "Polygon", coordinates: coordinates };
}

/**
 * Converts an RGeo polygon string to lat, long
 * @param {string} rgeo - polygon in RGeo String format
 * @returns {[number, number][][]} [[[latitude, longitude], ...], ...]
 */
export function rgeoToPolygon(rgeo) {
  return geoJSONPolygonToLatLngs(rgeoPolygonToGeoJSON(rgeo));
}

/**
 * Converts GeoJSON Polygon to array of [[latitude, longitude], ...]
 * @param {object} geoJSON - polygon in GeoJSON format
 * @returns {[number, number][][]} [[[latitude, longitude], ...], ...]
 */
export function geoJSONPolygonToLatLngs(geoJSON) {
  return geoJSON.coordinates.map(points => points.map(([lng, lat]) => [lat, lng]));
}

/**
 * Converts an array of Leaflet points to an array of [latitude, longitude]
 * @param {L.LatLng[][]} points - array of Leaflet coordinates
 * @returns {[number, number][][]} [[[latitude, longitude], ...], ...]
 */
export function leafletPolygonToLatLngs(polygon) {
  return polygon.map(points => points.map(({ lat, lng }) => [lat, lng]));
}

/**
 * Converts an array of Leaflet coordinates to GeoJSON format
 * @param {L.LatLng[][]} points - array of Leaflet coordinates
 * @returns {object} GeoJSON Polygon
 */
export function leafletPolygonToGeoJSON(coordinates) {
  return { type: "Polygon", coordinates: coordinates.map(points => points.map(({ lat, lng }) => [lng, lat])) };
}

/**
 * Converts an array of Leaflet points to an RGeo polygon string
 * @param {L.LatLng[][]} points - array of Leaflet coordinates
 * @returns {string} - polygon in RGeo String format
 */
export function leafletPolygonToRGeo(polygon) {
  return latLngsToRgeo(leafletPolygonToLatLngs(polygon));
}

/**
 * Converts geospatial coordinates into cartesian coordinates using web-mercator projection
 * @param {number} lat latitude
 * @param {number} lng longitude
 * @returns {[number, number]} [x, y]
 */
export function projectWebMercator(lat, lng) {
  let siny = Math.sin(lat * Math.PI / 180);

  // Truncating to 0.9999 effectively limits latitude to 89.189. This is
  // about a third of a tile past the edge of the world tile.
  siny = Math.min(Math.max(siny, -0.9999), 0.9999);

  return [
    (0.5 + lng / 360),
    (0.5 - Math.log((1 + siny) / (1 - siny)) / (4 * Math.PI))
  ];
}

/**
 * Converts cartesian coordinates into geospatial coordinates using web-mercator projection
 * @param {number} x X-coordinate
 * @param {number} y Y-coordinate
 * @returns {[number, number]} [latitude, longitude]
 */
export function unprojectWebMercator(x, y) {
  let phi = Math.exp(-4 * Math.PI * (y - 0.5));
  let lat = (180 * Math.asin((phi - 1) / (phi + 1))) / Math.PI;

  let lng = 360 * (x - 0.5);

  return [lat, lng];
}
